import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Page, Card, EmptyState, CodeView, Select } from '../ui';

interface TreeNode {
  name: string;
  path: string;
  type: 'dir' | 'file';
  children?: TreeNode[];
}

/** Constrói a árvore aninhada a partir da lista achatada de caminhos. */
function buildTree(files: string[]): TreeNode[] {
  const root: TreeNode = { name: '', path: '', type: 'dir', children: [] };
  for (const file of files) {
    const parts = file.split('/');
    let node = root;
    let acc = '';
    parts.forEach((part, i) => {
      acc = acc ? `${acc}/${part}` : part;
      const isFile = i === parts.length - 1;
      let child = node.children!.find((c) => c.name === part && c.type === (isFile ? 'file' : 'dir'));
      if (!child) {
        child = { name: part, path: acc, type: isFile ? 'file' : 'dir', ...(isFile ? {} : { children: [] }) };
        node.children!.push(child);
      }
      node = child;
    });
  }
  const sort = (nodes: TreeNode[]): TreeNode[] => {
    nodes.sort((a, b) => (a.type !== b.type ? (a.type === 'dir' ? -1 : 1) : a.name.localeCompare(b.name)));
    for (const n of nodes) if (n.children) sort(n.children);
    return nodes;
  };
  return sort(root.children!);
}

function FileTree({
  nodes, depth, expanded, openFile, onToggle, onOpen,
}: {
  nodes: TreeNode[]; depth: number; expanded: Set<string>; openFile: string | null;
  onToggle: (p: string) => void; onOpen: (p: string) => void;
}) {
  return (
    <>
      {nodes.map((n) => {
        const pad = { paddingLeft: 8 + depth * 14 } as const;
        if (n.type === 'dir') {
          const open = expanded.has(n.path);
          return (
            <Fragment key={n.path}>
              <button type="button" className="code-tree-row" style={pad} onClick={() => onToggle(n.path)}>
                {open ? '📂' : '📁'} {n.name}
              </button>
              {open && n.children && (
                <FileTree nodes={n.children} depth={depth + 1} expanded={expanded} openFile={openFile} onToggle={onToggle} onOpen={onOpen} />
              )}
            </Fragment>
          );
        }
        return (
          <button
            key={n.path}
            type="button"
            className={`code-tree-row code-tree-file ${openFile === n.path ? 'is-active' : ''}`}
            style={pad}
            onClick={() => onOpen(n.path)}
          >
            📄 {n.name}
          </button>
        );
      })}
    </>
  );
}

export function Code() {
  const { slug } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const refParam = params.get('ref') ?? '';

  const [files, setFiles] = useState<string[]>([]);
  const [baseBranch, setBaseBranch] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openFile, setOpenFile] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingFile, setLoadingFile] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    api.codeBranches(slug).then(({ branches }) => setBranches(branches)).catch(() => undefined);
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setErr(null);
    api
      .codeTree(slug, refParam || undefined)
      .then(({ files, baseBranch }) => {
        setFiles(files);
        setBaseBranch(baseBranch);
      })
      .catch((e) => setErr((e as { error?: string }).error === 'repo_unavailable' ? 'Repositório indisponível (clone falhou?).' : 'Falha ao carregar a árvore.'))
      .finally(() => setLoading(false));
  }, [slug, refParam]);

  function open(path: string) {
    if (!slug) return;
    setOpenFile(path);
    setLoadingFile(true);
    api
      .codeFile(slug, path, refParam || undefined)
      .then(({ content }) => setContent(content))
      .catch((e) => {
        const c = (e as { error?: string }).error;
        setContent(c === 'too_large' ? '// arquivo muito grande para exibir' : c === 'not_found' ? '// não encontrado' : '// (binário ou erro ao ler)');
      })
      .finally(() => setLoadingFile(false));
  }

  const tree = useMemo(() => buildTree(files), [files]);

  function selectRef(r: string) {
    setParams(r ? { ref: r } : {});
    setOpenFile(null);
    setContent('');
  }

  const currentRef = refParam || baseBranch;
  const branchOptions = branches.includes(currentRef) || !currentRef ? branches : [currentRef, ...branches];

  return (
    <Page
      title="Código"
      subtitle="Navegue o repositório do projeto (read-only). Os diffs de cada PR ficam no report."
      actions={
        <div className="code-ref-box">
          <span className="code-ref-label">⎇</span>
          <Select value={currentRef} onChange={(e) => selectRef(e.target.value)} disabled={branches.length === 0} style={{ minWidth: 200 }}>
            {branchOptions.length === 0 && <option value="">carregando…</option>}
            {branchOptions.map((b) => (
              <option key={b} value={b}>{b}{b === baseBranch ? ' (base)' : ''}</option>
            ))}
          </Select>
        </div>
      }
    >
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : files.length === 0 && !err ? (
        <EmptyState title="Repositório vazio" description="Nenhum arquivo encontrado nesse ref." />
      ) : (
        <div className="code-layout">
          <Card className="code-tree">
            <div className="code-tree-head">{refParam || baseBranch || 'HEAD'} · {files.length} arquivos</div>
            <div className="code-tree-scroll">
              <FileTree
                nodes={tree}
                depth={0}
                expanded={expanded}
                openFile={openFile}
                onToggle={(p) => setExpanded((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; })}
                onOpen={open}
              />
            </div>
          </Card>
          <Card className="code-main">
            {openFile ? (
              <>
                <div className="code-main-head"><strong>{openFile}</strong></div>
                {loadingFile ? <div className="center">Carregando…</div> : <CodeView code={content} filename={openFile} />}
              </>
            ) : (
              <EmptyState title="Selecione um arquivo" description="Escolha um arquivo na árvore para visualizar com destaque de sintaxe." />
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}
